/**
 * Plan Workout Completed Trigger Component
 *
 * Trigger: "completed a plan workout"
 * Shows: plan select + workout select (grouped by week)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import {
  fetchPlanWorkoutsById,
  fetchWorkoutPlanOptions,
} from 'src/models/workout-plan/workout-plan';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { TriggerComponentProps } from '../automationTriggers';

export const PlanWorkoutCompletedTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const { data: plans = [] } = useQuery({
    queryKey: ['workout-plans', 'options'],
    queryFn: () => fetchWorkoutPlanOptions(),
    staleTime: 60000,
  });

  const planId = automation.reminderPlanId;
  const { data: workouts = [] } = useQuery({
    queryKey: ['plan-workouts', planId],
    queryFn: () => fetchPlanWorkoutsById({ planId: planId! }),
    enabled: !!planId,
    staleTime: 60000,
  });

  const planOptions = plans.map((plan) => ({
    value: String(plan.id),
    label: plan.name,
  }));

  const workoutOptions = workouts.map((workout) => ({
    value: String(workout.id),
    label: `${t('automations.planWorkout.week', { week: workout.week })}: ${workout.name}`,
  }));

  const selectedPlan = plans.find(
    (plan) => String(plan.id) === String(automation.reminderPlanId)
  );
  const selectedWorkout = workouts.find(
    (workout) => String(workout.id) === String(automation.reminderTypeId)
  );

  useEffect(() => {
    if (!readOnly && !automation.reminderPlanId && plans.length > 0) {
      setFieldValue(`automations.${index}.reminderPlanId`, plans[0].id);
      setFieldValue(`automations.${index}.reminderTypeId`, null);
    }
  }, [
    automation.reminderPlanId,
    index,
    plans,
    readOnly,
    setFieldValue,
  ]);

  if (readOnly) {
    if (selectedPlan?.name && selectedWorkout?.name) {
      return (
        <span>
          {t('automations.triggers.completedPlanWorkoutNamed', {
            plan: selectedPlan.name,
          })}{' '}
          ({selectedWorkout.name})
        </span>
      );
    }

    if (selectedPlan?.name) {
      return (
        <span>
          {t('automations.triggers.completedPlanWorkoutNamed', {
            plan: selectedPlan.name,
          })}
        </span>
      );
    }

    return <span>{t('automations.triggers.completedPlanWorkout')}</span>;
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 300 }}>
        <FormSelect
          name={`automations.${index}.reminderPlanId`}
          label={t('automations.triggerLabels.plan')}
          options={planOptions}
          prompt={t('automations.triggerLabels.selectPlan')}
          onChange={(event) => {
            setFieldValue(
              `automations.${index}.reminderPlanId`,
              event.target.value || null
            );
            setFieldValue(`automations.${index}.reminderTypeId`, null);
          }}
        />
      </Box>
      {planId && (
        <Box sx={{ maxWidth: 300 }}>
          <FormSelect
            name={`automations.${index}.reminderTypeId`}
            label={t('automations.triggerLabels.workout')}
            options={workoutOptions}
            prompt={t('automations.triggerLabels.selectWorkout')}
          />
        </Box>
      )}
    </Stack>
  );
};
